import { useAtom } from '@reatom/npm-react'

import { Numeric } from '@blade/ui-kit'
import { PairLiquidityFormatted } from '@root/entities/pair/ui/common/pair-liquidity-formatted'
import { Trend } from '@root/entities/trend'
import { FavoriteUnavailable } from '@root/features/favorite-pairs/model/favorite-unavailable'

import { FavoriteActionsCell } from './favorite-action-cell'
import { FavoriteCurrencyCell } from './favorite-currency-cell'
import { FavoriteHoldingCell } from './favorite-holding-cell'
import { FavoriteMcCell } from './favorite-mc-cell'
import { EmptyCell, TextCell } from './favorite-table-common'
import { FavoriteTablePnlCell } from './favorite-table-pnl-cell'
import { FavoriteUnavailableCurrencyCell } from './favorite-unavailable-currency-cell'

import type { FavoritePairsTableColumn } from '../../../atoms/reatom-favorite-pairs-table'
import type { PairModel } from '@root/entities/pair'
import type { Column } from '@root/shared/ui-kit'

const LiquidityCell = ({ value }: { value: PairModel }) => {
  return (
    <TextCell>
      <PairLiquidityFormatted pair={value} />
    </TextCell>
  )
}

const PriceChangeCell = ({ value }: { value: PairModel }) => {
  const [priceChange] = useAtom(value.stats['5M'].priceChange)
  const trend = Trend.of(priceChange)
  return (
    <TextCell data-trend={trend}>
      <Numeric forceSign value={priceChange} preset="trend-change-percents" />%
    </TextCell>
  )
}

export const FavoriteTableColumn = {
  MarketCap: {
    id: 'mc',
    header: ({ t }) => t('trade.market-cap-short'),
    render: ({ value }) => {
      if (value instanceof FavoriteUnavailable) {
        return <EmptyCell />
      }
      return <FavoriteMcCell value={value} />
    },
    width: 80,
    sortable: true,
  },
  Liquidity: {
    id: 'liquidity',
    header: ({ t }) => t('common.liquidity'),
    render: ({ value }) => {
      if (value instanceof FavoriteUnavailable) {
        return <EmptyCell />
      }
      return <LiquidityCell value={value} />
    },
    width: 110,
    sortable: true,
  },
  Holding: {
    id: 'holding',
    header: ({ t }) => t('common.holding'),
    render: ({ value }) => {
      if (value instanceof FavoriteUnavailable) {
        return <EmptyCell />
      }
      return <FavoriteHoldingCell value={value} />
    },
    width: 110,
    sortable: true,
  },
  PriceChange: {
    id: 'priceChangePercent',
    header: ({ t }) => t('common.5m-percent'),
    render: ({ value }) => {
      if (value instanceof FavoriteUnavailable) {
        return <EmptyCell />
      }
      return <PriceChangeCell value={value} />
    },
    minWidth: 80,
    sortable: true,
  },
} satisfies Record<
  string,
  Column<PairModel | FavoriteUnavailable, FavoritePairsTableColumn>
>

export const favoritePairsColumns: Array<
  Column<PairModel | FavoriteUnavailable, FavoritePairsTableColumn>
> = [
  {
    id: 'currency',
    header: ({ t }) => t('token'),
    render: ({ value }) => {
      if (value instanceof FavoriteUnavailable) {
        return <FavoriteUnavailableCurrencyCell value={value} />
      }
      return <FavoriteCurrencyCell value={value} />
    },
    minWidth: 260,
  },
  FavoriteTableColumn.MarketCap,
  FavoriteTableColumn.Liquidity,
  FavoriteTableColumn.Holding,
  FavoriteTableColumn.PriceChange,

  {
    id: 'pnl',
    header: ({ t }) => t('common.upnl'),
    render: ({ value }) => {
      if (value instanceof FavoriteUnavailable) {
        return <EmptyCell />
      }
      return <FavoriteTablePnlCell pair={value} />
    },
    minWidth: 80,
  },
  {
    id: 'action',
    header: '',
    render: ({ value }) => {
      if (value instanceof FavoriteUnavailable) {
        return <EmptyCell />
      }
      return <FavoriteActionsCell pair={value} />
    },
    width: 210,
    justify: 'end',
    sortable: false,
  },
]
